import { stableSiteImagePath } from "@/data/stableSiteImages";
import UtilBar, { LivoniaBannerCard } from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import Hero from "@/components/Hero";
import TrustBar from "@/components/TrustBar";
import AwardSection from "@/components/AwardSection";
import DirectionsSection from "@/components/DirectionsSection";
import Statement from "@/components/Statement";
import AboutSection from "@/components/AboutSection";
import ServicesSection from "@/components/ServicesSection";
import HowWeWork from "@/components/HowWeWork";
import FullServiceCallout from "@/components/FullServiceCallout";
import ProductCatalog from "@/components/ProductCatalog";
import FlooringTypesSection from "@/components/FlooringTypesSection";

import RecentProjects from "@/components/RecentProjects";
import StatsBar from "@/components/StatsBar";
import TradeSection from "@/components/TradeSection";
import LivoniaProjectSection from "@/components/LivoniaProjectSection";
import FAQSection from "@/components/FAQSection";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import OurStoryTeaser from "@/components/OurStoryTeaser";

import SiteFooter from "@/components/SiteFooter";
import usePageSEO from "@/hooks/usePageSEO";
import allReviews from "@/data/reviewData";
import { HOME_SHARE_IMAGE, HOME_ORGANIZATION_ID, makeHomeImageSchemas, makeHomeWebPageSchema } from "@/data/homeProjectImages";

// Homepage-only review cards: same quotes as shown on the Plymouth, Birmingham
// and South Lyon city pages, labeled Livonia, MI per owner direction.
const livoniaReviewCards = ["Olga M.", "Leslie P.", "Janet N."].map((name) => {
  const review = allReviews.find((r) => r.name === name);
  return review ? { ...review, loc: "Livonia, MI" } : undefined;
}).filter((r): r is NonNullable<typeof r> => Boolean(r));

const homePhotoAssets = import.meta.glob([
  "/src/assets/pallmann-spider-planetary-sander-dustless-refinishing-michigan.webp",
  "/src/assets/northville-walnut-open-floor-kitchen.webp",
  "/src/assets/commercial-ann-arbor.webp",
  "/src/assets/reclaimed-tobacco-pine-floor-kitchen-wide-howell-mi.webp",
  "/src/assets/1-wood-custom-line-8-inch-white-oak-plymouth-mi.webp",
  "/src/assets/harbor-springs-invisible-maple-vaulted.webp",
  "/src/assets/viking-european-white-oak-birmingham-dining.webp",
  "/src/assets/harbor-springs-maple-great-room.webp",
], { eager: true, import: "default", query: "?url" });
const homeSchemas = [
  { "@context": "https://schema.org", "@type": "Organization", "@id": HOME_ORGANIZATION_ID, name: "1 Wood Floors", url: "https://1woodfloors.com" },
  ...makeHomeImageSchemas(name => {
    const src = stableSiteImagePath(name) ?? homePhotoAssets[`/src/assets/${name}.webp`];
    return typeof src === "string" ? `https://1woodfloors.com${src}` : undefined;
  }),
  makeHomeWebPageSchema(),
];

const Index = () => {
  // LocalBusiness + Organization schemas are baked into static HTML via generate-seo-html.mjs
  usePageSEO({
    title: "Hardwood Flooring Livonia & Southeast MI | 1 Wood Floors",
    description: "Livonia hardwood flooring showroom on Seven Mile Road serving Southeast Michigan. Four NWFA Certifications. Fully insured. 5.0 ★ · 104 Google Reviews.",
    path: "/",
    image: HOME_SHARE_IMAGE,
    jsonLd: homeSchemas,
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        <Hero />
        <div className="bg-neutral-950 px-4 md:px-12 py-3">
          <LivoniaBannerCard />
        </div>
        <TrustBar />
        <OurStoryTeaser />
        <Statement />
        <AboutSection />
        <ServicesSection />
        <HowWeWork />
        <FullServiceCallout variant="homepage" />
        <FlooringTypesSection />
        <ProductCatalog />
        
        <RecentProjects />
        <StatsBar />
        <TradeSection />
        <LivoniaProjectSection />
        <FAQSection />
        <GoogleReviewsCarousel count={6} extraReviews={livoniaReviewCards} />
        <DirectionsSection />
        <AwardSection />
      </main>
      <SiteFooter />
    </div>
  );
};

export default Index;
